/** @file Starts live sync for the loaded project: socket, frames, the session; returns the stop function. */
import { docStore } from '../../stores/docStore';
import { liveStore, type LiveStatus } from '../../stores/liveStore';
import { sessionStore } from '../../stores/sessionStore';
import { installHistory } from '../history/installHistory';
import { openLiveSocket, type LiveSocket, type SocketFactory, type SocketStatus, type TimerLike } from '../live/openLiveSocket';
import { feedPages } from './feedPages';
import { handleFrame } from './handleFrame';
import { liveSession, type SyncClient, type SyncSession } from './liveSession';
import { requestRenders } from './requestRenders';
import { sessionBatches } from './sessionBatches';
import { watchPresence } from './watchPresence';

/** What startLiveSync needs from the outside (browserSyncDeps in the app, fakes in tests). */
export interface LiveSyncDeps {
  client: SyncClient;
  url: string;
  createSocket: SocketFactory;
  timers: TimerLike;
  /** Asks before the page unloads while `blocked()` is true (beforeunload in the browser); returns its removal. */
  guardUnload: (blocked: () => boolean) => () => void;
}

const STATUS: Record<SocketStatus, LiveStatus> = { connecting: 'connecting', open: 'live', closed: 'offline' };

/** A session at the loaded rev, acting as the signed-in actor. */
function newSession(projectId: string, client: SyncClient, send: LiveSocket['send']): SyncSession {
  return {
    projectId, client, send,
    actor: sessionStore.getState().actor?.name ?? 'tk',
    knownRev: docStore.getState().slice?.rev ?? 0,
    busy: null, queue: Promise.resolve(), nextSeq: 1, created: new Map(), socketOpen: false, resyncWanted: false, hashDue: false, afterStop: null,
  };
}

/** Opens `/ws/<projectId>` (the slice must be loaded) and makes the session current until the returned stop runs. */
export function startLiveSync(projectId: string, deps: LiveSyncDeps): () => void {
  liveStore.setState(liveStore.getInitialState(), true);
  let socket: LiveSocket | null = null;
  const session = newSession(projectId, deps.client, (frame) => socket?.send(frame) ?? false);
  const current = (): boolean => liveSession.get() === session;
  liveSession.set(session);
  const unguard = deps.guardUnload(() => sessionBatches(session).some((b) => b.committedRev === null));
  const uninstallHistory = installHistory();
  const presence = watchPresence(session);
  void feedPages.seed(session);
  const reseed = liveStore.subscribe((s, before) => { if (s.epoch !== before.epoch && current()) void feedPages.seed(session); });
  socket = openLiveSocket({
    url: deps.url,
    create: deps.createSocket,
    timers: deps.timers,
    onFrame: (frame) => {
      if (!current()) return;
      handleFrame(session, frame);
      if (frame.t === 'hello') {
        presence.resend();
        requestRenders(null);
      }
    },
    onStatus: (status) => {
      if (!current()) return;
      session.socketOpen = status === 'open';
      liveStore.setState({ status: STATUS[status] });
    },
  });
  return () => {
    reseed();
    uninstallHistory();
    presence.stop();
    socket.close();
    // The queue keeps sending what was dispatched before leaving (finding 16); the guard stays until it drains.
    void session.queue.then(unguard);
    if (!current()) return;
    session.afterStop = liveStore.getState().batches.filter((b) => b.committedRev === null);
    liveSession.set(null);
    liveStore.setState(liveStore.getInitialState(), true);
  };
}
